<template>
  <div :style="getLogoRootStyle()">
    <label for="logo" :style="getLogoLabelStyle()">
      <img :src="getLogoQuiz()" alt="" :style="getLogoImageStyle()" />
    </label>

    <input
      @change="setImg($event, 'logo')"
      type="file"
      name="photoLogo"
      id="logo"
      accept="image/*"
      :disabled="selectedQuestion !== -1"
    />
  </div>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import style from "../sections/LeftPart/LeftPart.module.scss";
import {saveImgQuiz, modifyQuiz} from "@/page/quiz/scriptQuiz";
import {DOMAIN} from "@/http";

export default defineComponent({
  name: "LogoComponent",

  data() {
    return {
      style,
      saveImgQuiz,
      modifyQuiz,
      newLogo: "",
      cachedPreviewUrls: {} as Record<string, string>
    };
  },

  beforeUnmount() {
    this.revokePreviewUrls();
  },

  methods: {
    getCachedFilePreviewUrl(fileName: string): string {
      const cachedFile = this.cachedFiles.find((file: File) => {
        return file.name === fileName;
      });

      if (!cachedFile) {
        return "";
      }

      const currentPreviewUrl = this.cachedPreviewUrls[fileName];

      if (currentPreviewUrl) {
        return currentPreviewUrl;
      }

      const previewUrl = URL.createObjectURL(cachedFile);

      this.cachedPreviewUrls = {
        ...this.cachedPreviewUrls,
        [fileName]: previewUrl
      };

      return previewUrl;
    },

    revokePreviewUrls(): void {
      Object.values(this.cachedPreviewUrls).forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });

      this.cachedPreviewUrls = {};
    },

    getNormalizedRangeSize(): number {
      const value = Number(this.quiz?.rangeSize);

      if (!Number.isFinite(value)) {
        return 100;
      }

      return value;
    },

    getLogoHeight(): number {
      const baseHeight = 40;
      const rangeSize = this.getNormalizedRangeSize();

      return (baseHeight * rangeSize) / 100;
    },

    getLogoRootStyle(): string {
      if (!this.quiz) {
        return "display: none !important;";
      }

      const height = this.getLogoHeight();

      return `
        display: inline-flex !important;
        align-items: center !important;
        width: fit-content !important;
        height: ${height}px !important;
        min-height: ${height}px !important;
        max-height: none !important;
        line-height: 0 !important;
        overflow: visible !important;
      `;
    },

    getLogoLabelStyle(): string {
      const height = this.getLogoHeight();
      const cursor = this.selectedQuestion !== -1 ? "auto" : "pointer";

      return `
        cursor: ${cursor} !important;
        display: inline-flex !important;
        align-items: center !important;
        width: fit-content !important;
        height: ${height}px !important;
        min-height: ${height}px !important;
        max-height: none !important;
        line-height: 0 !important;
        overflow: visible !important;
        transform: none !important;
      `;
    },

    getLogoImageStyle(): string {
      const height = this.getLogoHeight();
      const background =
        this.selectedQuestion !== -1 ? "transparent" : "transparent";

      return `
        display: block !important;
        width: auto !important;
        height: ${height}px !important;
        min-height: ${height}px !important;
        max-height: none !important;
        object-fit: contain !important;
        background: ${background} !important;
        transform: none !important;
      `;
    },

    getLogoQuiz() {
      const logoImg = this.modifyQuiz("get", "", ["logo"]);

      if (!logoImg) {
        return "https://" + DOMAIN + "/api/img?name=readds.svg";
      }

      const cachedPreviewUrl = this.getCachedFilePreviewUrl(logoImg);

      if (cachedPreviewUrl) {
        return cachedPreviewUrl;
      }

      return "https://" + DOMAIN + "/api/img?name=" + logoImg;
    },

    async setImg(e: Event, name: string) {
      const target = e.target as HTMLInputElement;

      if (!target.files?.length) {
        return;
      }

      const file = target.files[0];
      const blob = file.slice(0, file.size, file.type);
      const nameSplit = file.name.split(".");
      const type = nameSplit[nameSplit.length - 1];
      const newName = name + this.quiz._id + "." + type;

      const newFile = new File([blob], newName, {
        type: file.type
      });

      const srcFile = URL.createObjectURL(newFile);

      if (name === "logo") {
        if (this.newLogo.startsWith("blob:")) {
          URL.revokeObjectURL(this.newLogo);
        }

        this.newLogo = srcFile;
        this.modifyQuiz("set", newName, ["logo"]);
      }

      this.cachedFiles.push(newFile);

      target.value = "";
    }
  },

  computed: {
    quiz() {
      return this.$quizStore.getQuiz;
    },

    mobileVersion() {
      return this.$quizStore.getMobileVersion;
    },

    selectedQuestion() {
      return this.$quizStore.getSelectedQuestion;
    },

    cachedFiles(): File[] {
      return this.$quizStore.getCachedFiles;
    }
  },

  watch: {
    cachedFiles(): void {
      this.revokePreviewUrls();
    }
  }
});
</script>
